/**
 * Per-context Right Panel card definitions for Client portal.
 *
 * #RIGHTRAIL-honesty FIX (AUDIT_MOCK_STUB P1): this file previously shipped a
 * fully HARDCODED per-persona rail — fake agenda meetings ("Comité conformité
 * Mer. 14h"), fabricated KPI numbers (76/100, 88%, "12 384" consents), named
 * incidents/models/tasks — rendered on EVERY page for EVERY tenant, so users
 * saw fabricated data as if it were their own live figures.
 *
 * There is no per-tenant right-rail summary endpoint to source these numbers,
 * so we no longer fabricate them. Each persona's rail is now a set of honest
 * NAVIGATIONAL quick-links (no numbers, no names, no fake records) pointing to
 * the real pages where the user's actual data lives. `MainLayout` additionally
 * prepends a live "Mes dossiers" card derived from the user's real dossier
 * store when dossiers exist (see MainLayout.tsx). If/when a real summary API is
 * added, feed it here as `kpi`/`list` cards.
 */

import {
  Sparkles, ClipboardCheck, Shield, Building2,
  Eye, BrainCircuit, Target, BadgeCheck, Activity,
} from 'lucide-react'
import type { ContextId } from '@/types/real-estate'
import type { RightPanelCard } from '@/components/layout/RightPanel'

// Shared quick-links shown for every persona (real destinations, no fake data).
const DEFAULT_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'quick-links',
    title: 'Accès rapides',
    icon: Sparkles,
    accent: 'blue',
    items: [
      { id: 'ql-dash', primary: 'Tableau de bord', href: '/dashboard' },
      { id: 'ql-assess', primary: 'Mes évaluations', href: '/assessments' },
      { id: 'ql-tasks', primary: 'Mes tâches', href: '/dashboard' },
    ],
  },
]

const DIRIGEANT_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'exec-links',
    title: 'Pilotage exécutif',
    icon: Target,
    accent: 'amber',
    items: [
      { id: 'e1', primary: 'Score GRC & maturité', href: '/dashboard-pro/score' },
      { id: 'e2', primary: 'Initiatives stratégiques', href: '/strategy-pro/initiatives' },
      { id: 'e3', primary: 'Registre des risques', href: '/risks' },
      { id: 'e4', primary: 'Tableau de bord C-Suite', href: '/dashboard-pro/csuite' },
    ],
  },
  ...DEFAULT_CARDS,
]

const CISO_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'sec-links',
    title: 'Sécurité',
    icon: Shield,
    accent: 'red',
    items: [
      { id: 's1', primary: 'Score & posture sécurité', href: '/security-pro/score' },
      { id: 's2', primary: 'Incidents', href: '/security-pro/incidents' },
      { id: 's3', primary: 'Vulnérabilités', href: '/security-pro/vulns' },
      { id: 's4', primary: 'Contrôles', href: '/security-pro/controls' },
    ],
  },
  ...DEFAULT_CARDS,
]

const DPO_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'privacy-links',
    title: 'Vie privée',
    icon: Eye,
    accent: 'purple',
    items: [
      { id: 'p1', primary: 'Demandes DSR', href: '/compliance/dsr' },
      { id: 'p2', primary: 'Consentements', href: '/consents/active' },
      { id: 'p3', primary: 'Analyses d\'impact (PIA)', href: '/compliance/pia' },
    ],
  },
  ...DEFAULT_CARDS,
]

const RISK_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'risk-links',
    title: 'Risques',
    icon: Activity,
    accent: 'red',
    items: [
      { id: 'r1', primary: 'Registre des risques', href: '/risk-pro/register' },
      { id: 'r2', primary: 'Indicateurs (KRI)', href: '/risk-pro/kri' },
      { id: 'r3', primary: 'Matrice des risques', href: '/risk-pro/matrix' },
      { id: 'r4', primary: 'Plans de traitement', href: '/risk-pro/treatment-plans' },
    ],
  },
  ...DEFAULT_CARDS,
]

const COMPLIANCE_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'compliance-links',
    title: 'Conformité',
    icon: BadgeCheck,
    accent: 'teal',
    items: [
      { id: 'c1', primary: 'Tableau de conformité', href: '/compliance' },
      { id: 'c2', primary: 'Obligations', href: '/compliance-pro/obligations' },
      { id: 'c3', primary: 'Actions correctives', href: '/compliance-pro/actions' },
      { id: 'c4', primary: 'Écarts (gaps)', href: '/compliance-pro/gaps' },
    ],
  },
  ...DEFAULT_CARDS,
]

const DATA_IA_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'data-ia-links',
    title: 'Data & IA',
    icon: BrainCircuit,
    accent: 'purple',
    items: [
      { id: 'd1', primary: 'Registre des modèles IA', href: '/ai-ia-pro/models' },
      { id: 'd2', primary: 'Qualité des données', href: '/data-pro/quality' },
      { id: 'd3', primary: 'Gouvernance des données', href: '/data-pro/governance' },
      { id: 'd4', primary: 'Conformité EU AI Act', href: '/ai-ia-pro/eu-ai-act' },
    ],
  },
  ...DEFAULT_CARDS,
]

const COLLABORATEUR_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'collab-links',
    title: 'Mon espace',
    icon: ClipboardCheck,
    accent: 'teal',
    items: [
      { id: 't1', primary: 'Mes tâches', href: '/dashboard' },
      { id: 't2', primary: 'Documents partagés', href: '/vault' },
      { id: 't3', primary: 'Mes évaluations', href: '/assessments' },
    ],
  },
]

const ADMIN_CARDS: RightPanelCard[] = [
  {
    kind: 'list',
    id: 'admin-links',
    title: 'Administration',
    icon: Building2,
    accent: 'slate',
    items: [
      { id: 'u1', primary: 'Équipe & utilisateurs', href: '/admin/users' },
      { id: 'u2', primary: 'Paramètres', href: '/admin/settings' },
    ],
  },
  ...DEFAULT_CARDS,
]

export function getRightPanelCardsForContext(
  contextId: ContextId,
): RightPanelCard[] {
  switch (contextId) {
    case 'dirigeant': return DIRIGEANT_CARDS
    case 'ciso': return CISO_CARDS
    case 'dpo': return DPO_CARDS
    case 'risk-manager': return RISK_CARDS
    case 'compliance-officer': return COMPLIANCE_CARDS
    case 'data-ia-officer': return DATA_IA_CARDS
    case 'collaborateur': return COLLABORATEUR_CARDS
    case 'administration': return ADMIN_CARDS
    default: return DEFAULT_CARDS
  }
}
